import React from 'react';
import { Card } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { getDayModeConfig } from '../utils/dayModes';
import { SkipForward } from 'lucide-react';

const TaskItem = ({ task, isCompleted, onToggle, modeConfig }) => {
  return (
    <div
      data-testid={`task-item-${task.id}`}
      className={`
        flex items-center gap-4 p-4 rounded-lg transition-all duration-300 border relative
        ${isCompleted 
          ? 'bg-paper-aged opacity-70 border-stone-200' 
          : task.skippable 
            ? 'bg-paper-light border-dashed border-stone-300' 
            : `${modeConfig.bgColor} border-${modeConfig.color}-border`
        }
      `}
    >
      {/* Decorative checkmark flourish */}
      {isCompleted && (
        <div className="absolute top-1 right-2 text-sm opacity-30">✓</div>
      )}
      
      <Checkbox
        data-testid={`task-checkbox-${task.id}`}
        checked={isCompleted}
        onCheckedChange={() => onToggle(task.id)}
        className={`
          task-checkbox w-6 h-6 rounded-full border-2
          ${isCompleted 
            ? 'bg-sage border-sage' 
            : `border-brown`
          }
        `}
      />
      
      <div className="flex-1">
        <p className={`
          font-medium transition-all font-sans
          ${isCompleted 
            ? 'text-ink-500 line-through' 
            : modeConfig.textColor
          }
        `}>
          {task.text}
        </p>
        {task.skippable && !isCompleted && (
          <p className="text-xs text-ink-500 flex items-center gap-1 mt-1 font-accent italic">
            <SkipForward className="w-3 h-3" />
            Can be skipped today
          </p>
        )}
      </div>
      
      <span className={`
        text-xs px-2 py-1 rounded-md font-medium font-accent
        ${task.skippable 
          ? 'bg-paper-aged text-ink-500 border border-stone-300' 
          : `${modeConfig.bgColor} ${modeConfig.textColor} border ${modeConfig.borderColor}`
        }
      `}>
        {task.priority}
      </span>
    </div>
  );
};

const TaskList = ({ dayMode, completedTasks, onToggleTask }) => {
  const modeConfig = getDayModeConfig(dayMode);
  
  if (!modeConfig) return null;
  
  const essentialTasks = modeConfig.tasks.filter(t => !t.skippable);
  const flexibleTasks = modeConfig.tasks.filter(t => t.skippable);
  const essentialCount = essentialTasks.filter(t => completedTasks.includes(t.id)).length;
  
  return (
    <div className="space-y-6" data-testid="task-list">
      {/* Progress indicator */}
      <Card className="p-6 bg-paper-light shadow-lifted border border-stone-200 relative overflow-hidden">
        {/* Decorative corner */}
        <div className="absolute top-2 right-2 text-lg opacity-20">📋</div>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-serif text-xl text-ink-900">Today&apos;s Flow</h3>
            <p className="text-sm text-ink-500 font-accent italic">
              {essentialCount} of {essentialTasks.length} essentials done
            </p>
          </div>
          <div className={`
            w-14 h-14 rounded-full flex items-center justify-center border-2
            ${modeConfig.bgColor} ${modeConfig.borderColor}
          `}>
            <span className="text-2xl">{modeConfig.emoji}</span>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="w-full h-2 bg-paper-aged rounded-full overflow-hidden">
          <div 
            className="h-full transition-all duration-500 rounded-full bg-brown"
            style={{ width: `${(essentialCount / essentialTasks.length) * 100}%` }}
          />
        </div>
      </Card>
      
      {/* Essential tasks */}
      <div className="space-y-3">
        <h4 className="font-serif font-medium text-ink-900 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-brown" />
          Enough for today
        </h4>
        {essentialTasks.map((task) => (
          <TaskItem
            key={task.id}
            task={task}
            isCompleted={completedTasks.includes(task.id)}
            onToggle={onToggleTask}
            modeConfig={modeConfig}
          />
        ))}
      </div>
      
      {/* Flexible tasks */}
      {flexibleTasks.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-ink-500 flex items-center gap-2 font-accent italic">
            <SkipForward className="w-4 h-4" />
            If you have capacity (okay to skip)
          </h4>
          {flexibleTasks.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              isCompleted={completedTasks.includes(task.id)}
              onToggle={onToggleTask}
              modeConfig={modeConfig}
            />
          ))}
        </div>
      )}
      
      {/* Skip message */}
      <div className={`
        p-4 rounded-lg text-center border
        ${modeConfig.bgColor} ${modeConfig.borderColor}
      `}>
        <p className={`text-sm font-accent italic ${modeConfig.textColor}`}>
          {modeConfig.skipMessage}
        </p>
      </div>
    </div>
  );
};

export default TaskList;
